import { createSelector } from 'reselect';
import moment from 'moment';

import makeSelectMain, {
  getMainClientInfo,
  getNextPaymentDueDetails,
  selectIsPastServicingPaymentCutOffDate,
} from '../../Main/selectors';
import makeSelectMobile from '../../Mobile/selectors';
import {
  convertNumToCurrencyStr,
  formatDate,
  interpolate,
  snakeCaseKeys,
} from '../../../utils/globalHelpers';
import {
  makeSelectChosenAccountInfo,
  makeSelectPayFromButtonProps,
  makeSelectUseAccountInfoExistingByPaymentType,
} from '../Sources/selectors';
import { CONFIRMATION_DICTIONARY_AUTOPAY } from './constants';
import { formatAutopayStatusProperties, getDelayDaysString } from './helpers';
import { initialState } from './reducer';
import { AUTOPAY_PAYMENT_STATUSES } from './statusDictionary';

/**
 * Selector for the whole Autopay state domain
 */
const selectAutopayDomain = (state) => state.autopay || initialState;

/**
 * Selector for Autopay state's main properties
 */
const makeSelectAutopay = (prop) => createSelector(
  selectAutopayDomain,
  (state) => state[prop],
);

/**
 * @description Selects values within the allowedSetting object if present
 */
const selectAllowedSetting = (prop) => createSelector(
  selectStatusDataProp('allowedSetting'),
  (allowedSetting) => allowedSetting ? allowedSetting[prop] : null,
);

/**
 * @description Selects data utilized by Autopay edit amount drawer on desktop
 */
const selectAmountDrawerProps = () => createSelector(
  selectSelectedAdditionalPrincipal(),
  selectFormErrors('amount'),
  selectStatusDataProp('regularMonthlyPayment'),
  getNextPaymentDueDetails(),
  selectAllowedSetting('maximumAdditionalPrincipal'),
  (
    additionalPrincipal,
    amountFormErrors,
    regularMonthlyPayment,
    { nextPaymentDueDate },
    maximumAdditionalPrincipal,
  ) => ({
    additionalPrincipal,
    amountFormErrors,
    dueDate: formatDate(nextPaymentDueDate, 'UTC', 'LL'),
    maxAdditionalPrincipal: maximumAdditionalPrincipal,
    regularMonthlyPayment: convertNumToCurrencyStr(regularMonthlyPayment),
  }),
);

/**
 * @description selects flow based on allowed operation
 */
const selectAutopayFlow = () => createSelector(
  selectStatusDataProp('allowedOperation'),
  (allowedOperation) => {
    if (allowedOperation === 'Request_Enable') return 'enable';
    if (allowedOperation === 'Request_Change_Or_Stop') return 'change';
    if (allowedOperation === 'Request_Cancel') return 'cancel';
    return '';
  },
);

/**
 * @description formats begin date values for ui picker component
 */
const selectAvailableBeginDates = () => createSelector(
  selectStatusDataProp('allowedSetting'),
  makeSelectMobile('isMobile'),
  (allowedSetting, isMobile) => {
    const { availableBeginDates } = allowedSetting || {};
    if (availableBeginDates) {
      const key = isMobile ? 'label' : 'display';
      return availableBeginDates.map((date) =>
        ({ [key]: formatDate(date, 'UTC', 'LL'), value: date }));
    }
    return [];
  },
);

/**
 * @description formats delay days values for ui picker component
 */
const selectAvailableDelayDays = () => createSelector(
  selectStatusDataProp('allowedSetting'),
  makeSelectMobile('isMobile'),
  (allowedSetting, isMobile) => {
    const { availableDelayDays } = allowedSetting || {};
    if (availableDelayDays) {
      const key = isMobile ? 'label' : 'display';
      return availableDelayDays.map((day) =>
        ({ [key]: `${day} ${day === 1 ? 'Day' : 'Days'}`, value: day }));
    }
    return [];
  },
);

const selectCancelConfirmation = () => createSelector(
  makeSelectAutopay('cancelConfirmation'),
  (cancelConfirmationData) => {
    if (cancelConfirmationData) {
      const {
        amount,
        confirmationNumber = '',
        date,
        status,
      } = cancelConfirmationData;
      return {
        amount: convertNumToCurrencyStr(amount),
        confirmationNumber,
        date: formatDate(date, 'UTC', 'LL'),
        status,
      };
    }
    return false;
  },
);

/**
 * @description formats data for confirmation component
 */
const selectConfirmationData = () => createSelector(
  makeSelectAutopay('confirmationNumber'),
  makeSelectAutopay('confirmationType'),
  makeSelectAutopay('prevConfirmationType'),
  selectFormattedBeginDate(),
  selectFormattedDelayDays(),
  makeSelectPayFromButtonProps('autopay'),
  selectPaymentAmount(),
  (
    confirmationNumber,
    confirmationType,
    prevConfirmationType,
    formattedBeginDate,
    formattedDelayDays,
    { value: selectedPaymentSourceString },
    paymentAmount,
  ) => {
    if (!confirmationNumber) {
      return false;
    }
    const { labelArray } =
      CONFIRMATION_DICTIONARY_AUTOPAY[prevConfirmationType || confirmationType];

    return labelArray.map(({ key, label }) => {
      const obj = { label, value: '' };
      if (key === 'beginDate') obj.value = formattedBeginDate;
      if (key === 'confirmationNumber') obj.value = confirmationNumber;
      if (key === 'delayDays') obj.value = formattedDelayDays;
      if (key === 'paymentSource') obj.value = selectedPaymentSourceString;
      if (key === 'totalPayment') obj.value = paymentAmount;
      return obj;
    }).filter(({ value }) => !!value);
  },
);

/**
 * @description formats language for disclosure component
 */
const selectDisclosureLanguage = () => createSelector(
  selectSummaryScheduledDateString(),
  selectPaymentAmount(),
  (summarySubText, summaryText) => ({
    headerText: 'Automatic Payment Terms & Conditions',
    summaryButtonLabel: 'Accept Terms',
    summarySubText,
    summaryText,
  }),
);

/**
 * @description selects error value via prop passed
 */
const selectError = (prop) => createSelector(
  makeSelectAutopay('errors'),
  (errors) => errors[prop],
);

/**
 * @description selects form data via prop
 */
const selectForm = (prop) => createSelector(
  makeSelectAutopay('form'),
  (form) => form[prop],
);

/**
 * @description selects selected date and formats to be displayed on ui
 */
const selectFormattedBeginDate = () => createSelector(
  selectSelectedBeginDate(),
  (selectedBeginDate) =>
    selectedBeginDate ? formatDate(selectedBeginDate, 'UTC', 'LL') : '',
);

/**
 * @description selects form error data via prop
 */
const selectFormErrors = (prop) => createSelector(
  makeSelectAutopay('formErrors'),
  (form) => form[prop],
);

/**
 * @description selects delay days value and formats as string for ui
 */
const selectFormattedDelayDays = () => createSelector(
  selectSelectedDelayDays(),
  (selectedDelayDays) => {
    if (!selectedDelayDays) {
      return 'No Delay';
    }
    return getDelayDaysString(selectedDelayDays);
  },
);

const selectIsEligibleForAutopay = () => createSelector(
  selectStatusDataProp('allowedOperation'),
  (allowedOperation) => allowedOperation === 'Request_Enable',
);

const selectIsAbleToAccessAutopay = () => createSelector(
  selectStatusDataProp('allowedOperation'),
  makeSelectAutopay('desktopView'),
  makeSelectAutopay('step'),
  (allowedOperation, desktopView, step) => {
    if (allowedOperation === 'Request_Cancel') {
      if (step === 4) return true;
      if (desktopView === 'cancel') return true;
      return false;
    }
    return allowedOperation !== 'None';
  },
);

const selectIsAutopayEnabledOrPending = (prop) => createSelector(
  makeSelectAutopay('statusData'),
  (autopayStatus) => ['Enabled', 'Enable_Requested'].includes(autopayStatus[prop]),
);

const selectIsModalOpen = (prop) => createSelector(
  makeSelectAutopay('isModalOpen'),
  (isModalOpen) => isModalOpen[prop],
);

/**
 * @description selects maximum number of delay days allowed
 */
const selectMaxDelayDays = () => createSelector(
  selectStatusDataProp('allowedSetting'),
  (allowedSetting) => {
    const { availableDelayDays } = allowedSetting || {};
    if (availableDelayDays && availableDelayDays.length) {
      return availableDelayDays[availableDelayDays.length - 1];
    }
    return 0;
  },
);

/**
 * @description derives autopay payment amount
 */
const selectPaymentAmount = () => createSelector(
  selectSelectedAdditionalPrincipal(),
  selectStatusDataProp('regularMonthlyPayment'),
  (selectedAdditionalPrincipal, regularMonthlyPayment = 0) =>
    convertNumToCurrencyStr(regularMonthlyPayment + selectedAdditionalPrincipal),
);

/**
 * @description formats data for enable and change autopay request
 */
const selectRequestBody = () => createSelector(
  makeSelectChosenAccountInfo('autopay'),
  makeSelectUseAccountInfoExistingByPaymentType('autopay'),
  selectSelectedAdditionalPrincipal(),
  selectSelectedBeginDate(),
  selectSelectedDelayDays(),
  (
    sourcesAccountInfo,
    useAccountInfoExisting,
    selectedAdditionalPrincipal,
    selectedBeginDate,
    selectedDelayDays,
  ) => {
    if (useAccountInfoExisting) {
      return snakeCaseKeys({
        additionalPrincipal: selectedAdditionalPrincipal,
        beginDate: selectedBeginDate,
        delayDays: selectedDelayDays,
        useExistingAccountInformation: true,
      });
    }
    return snakeCaseKeys({
      ...sourcesAccountInfo,
      additionalPrincipal: selectedAdditionalPrincipal,
      beginDate: selectedBeginDate,
      delayDays: selectedDelayDays,
    });
  },
);

/**
 * @description selects appropriate additional principal value to use
 */
const selectSelectedAdditionalPrincipal = () => createSelector(
  makeSelectAutopay('enableRequestData'),
  selectForm('amount'),
  selectStatusDataProp('currentSetting'),
  (enableRequestData, { additionalPrincipal: formAdditionalPrincipal }, currentSetting) => {
    const { additionalPrincipal: requestAdditionalPrincipal } = enableRequestData || {};
    if (requestAdditionalPrincipal || requestAdditionalPrincipal === 0) {
      return requestAdditionalPrincipal;
    }

    if (formAdditionalPrincipal || formAdditionalPrincipal === 0) {
      return formAdditionalPrincipal;
    }

    const { additionalPrincipal: currentAdditionalPrincipal } = currentSetting || {};
    if (currentAdditionalPrincipal || currentAdditionalPrincipal === 0) {
      return currentAdditionalPrincipal;
    }

    return 0;
  },
);

/**
 * @description selects appropriate begin date value to be displayed
 */
const selectSelectedBeginDate = () => createSelector(
  makeSelectAutopay('enableRequestData'),
  selectForm('account'),
  selectStatusDataProp('allowedSetting'),
  selectStatusDataProp('currentSetting'),
  (
    enableRequestData,
    { beginDate: formBeginDate },
    allowedSetting,
    currentSetting,
  ) => {
    const { beginDate: requestBeginDate } = enableRequestData || {};
    if (requestBeginDate) {
      return requestBeginDate;
    }

    if (formBeginDate) {
      return formBeginDate;
    }

    const { beginDate } = currentSetting || {};
    if (beginDate) {
      return beginDate;
    }

    const { availableBeginDates } = allowedSetting || {};
    const [firstAvailableBeginDate] = availableBeginDates || [];
    if (firstAvailableBeginDate) {
      return firstAvailableBeginDate;
    }

    return '';
  },
);

/**
 * @description selects appropriate delay days value to be displayed
 */
const selectSelectedDelayDays = () => createSelector(
  makeSelectAutopay('enableRequestData'),
  selectForm('account'),
  selectStatusDataProp('allowedSetting'),
  selectStatusDataProp('currentSetting'),
  (
    enableRequestData,
    { delayDays: formDelayDays },
    allowedSetting,
    currentSetting,
  ) => {
    const { delayDays: requestDelayDays } = enableRequestData || {};
    if (requestDelayDays || requestDelayDays === 0) {
      return requestDelayDays;
    }

    if (formDelayDays || formDelayDays === 0) {
      return formDelayDays;
    }

    const { delayDays } = currentSetting || {};
    if (delayDays || delayDays === 0) {
      return delayDays;
    }

    const { availableDelayDays } = allowedSetting || {};
    const [firstAvailableDelayDay] = availableDelayDays || [];
    if (firstAvailableDelayDay || firstAvailableDelayDay === 0) {
      return firstAvailableDelayDay;
    }

    return 0;
  },
);

/**
 * @description formats setup autopay review data to be displayed
 */
const selectSetupAutopayReviewData = () => createSelector(
  selectPaymentAmount(),
  selectFormattedBeginDate(),
  selectFormattedDelayDays(),
  selectStatusDataProp('allowedSetting'),
  (paymentAmount, formattedBeginDate, formattedDelayDays, allowedSetting) => {
    const { canSetBeginDate, canSetDelayDays } = allowedSetting || {};
    const reviewData = [{
      label: 'Payment Amount',
      value: paymentAmount,
    }];
    if (canSetBeginDate) {
      reviewData.push({
        label: 'Start Date',
        value: formattedBeginDate,
      });
    }
    if (canSetDelayDays) {
      reviewData.push({
        label: 'Delay Payment',
        value: formattedDelayDays,
      });
    }
    return reviewData;
  },
);

/**
 * @description formats edit autopay review data to be displayed
 */
const selectEditAutopayReviewData = () => createSelector(
  makeSelectPayFromButtonProps('autopay'),
  selectFormattedBeginDate(),
  selectSelectedDelayDays(),
  selectPaymentAmount(),
  selectStatusDataProp('allowedSetting'),
  selectStatusDataProp('currentSetting'),
  (
    { value: selectedPaymentSourceString },
    formattedBeginDate,
    selectedDelayDays,
    selectedPaymentAmount,
    allowedSetting,
    currentSetting,
  ) => {
    const { canSetBeginDate, canSetDelayDays } = allowedSetting || {};
    const {
      additionalPrincipal,
      beginDate,
      delayDays,
      isSavedSavings,
      maskedAccountNumber,
      regularMonthlyPayment,
    } = currentSetting || {};

    const arrToReturn = [
      {
        label: 'Payment Amount',
        previousValue:
          convertNumToCurrencyStr(regularMonthlyPayment + (additionalPrincipal || 0)),
        value: selectedPaymentAmount,
      },
      {
        label: 'Payment Source',
        previousValue:
          maskedAccountNumber && isSavedSavings !== null ?
            `${isSavedSavings ? 'Savings' : 'Checking'} (...${maskedAccountNumber.slice(-4)})` :
            null,
        value: selectedPaymentSourceString,
      },
    ];

    if (canSetBeginDate && formattedBeginDate) {
      const previousBeginDate = beginDate ? formatDate(beginDate, 'UTC', 'LL') : null;

      arrToReturn.push({
        label: 'Start Date',
        previousValue: previousBeginDate,
        value: formattedBeginDate,
      });
    }

    if (canSetDelayDays) {
      const previousDelayDays = delayDays !== null ? getDelayDaysString(delayDays) : null;

      arrToReturn.push({
        label: 'Delay Payment',
        previousValue: previousDelayDays,
        value: getDelayDaysString(selectedDelayDays),
      });
    }

    return arrToReturn;
  },
);

/**
 * @description selects statusData value via prop
 */
const selectStatusDataProp = (prop) => createSelector(
  makeSelectAutopay('statusData'),
  (statusData) => statusData[prop],
);

const selectStatusMessageProps = () => createSelector(
  selectStatusDataProp('status'),
  selectStatusDataProp('currentSetting'),
  getMainClientInfo('customerServicePhoneNumber'),
  makeSelectMobile('isMobile'),
  makeSelectMain('loanDetails'),
  selectIsPastServicingPaymentCutOffDate(),
  (
    autopayStatus,
    currentSetting,
    customerServicePhoneNumber,
    isMobile,
    { summary: { automaticPaymentDrafting } },
    isPastServicingPaymentCutOffDate,
  ) => {
    let statusToUse = autopayStatus;
    if (isPastServicingPaymentCutOffDate) {
      statusToUse = 'Not_Available';
    } else if (autopayStatus === 'Not_Available' && automaticPaymentDrafting === 'Yes') {
      statusToUse = 'Not_Available_And_Automatic_Payment_Drafting';
    }

    const props = isMobile ?
      AUTOPAY_PAYMENT_STATUSES.mobile[statusToUse] :
      AUTOPAY_PAYMENT_STATUSES.desktop[statusToUse];
    if (!props && !isPastServicingPaymentCutOffDate) return null;

    const { message } = props;
    const {
      cancellableDate,
      nextDraftDate,
      scheduledDate,
      totalMonthlyPayment,
    } =
      formatAutopayStatusProperties(currentSetting) || {};
    const formattedMessage = interpolate(message, {
      cancellableDate,
      customerServicePhoneNumber,
      nextDraftDate,
      scheduledDate,
      totalMonthlyPayment,
    });

    if (isMobile) {
      const { button, header, iconType } = props;
      return {
        button,
        header,
        iconType,
        message: formattedMessage,
      };
    }

    const {
      header,
      iconName,
      linkLabel,
      operation,
    } = props;
    return {
      header,
      iconName,
      linkLabel,
      operation,
      text: formattedMessage,
    };
  },
);

/**
 * @description selects status value via prop
 */
const selectStatusProp = (prop) => createSelector(
  makeSelectAutopay('status'),
  (status) => status[prop],
);

/**
 * @description formats string to be displayed in payment summary component
 */
const selectSummaryScheduledDateString = () => createSelector(
  selectSelectedBeginDate(),
  selectSelectedDelayDays(),
  getNextPaymentDueDetails(),
  (
    selectedBeginDate,
    selectedDelayDays,
    { nextPaymentDueDate },
  ) => {
    if (!selectedDelayDays) {
      const beginDateIsDueDate = moment(selectedBeginDate)
        .isSame(moment(nextPaymentDueDate), 'date');
      if (beginDateIsDueDate) {
        return `Due on ${formatDate(nextPaymentDueDate, 'UTC', 'LL')}`;
      }
      if (selectedBeginDate) {
        return `Autopay starts ${formatDate(selectedBeginDate, 'UTC', 'LL')}`;
      }
    }
    if (selectedDelayDays === 0) return 'Scheduled for due date';
    return `Scheduled for ${getDelayDaysString(selectedDelayDays)}`;
  },
);

const selectAutopayStatus = () => createSelector(
  selectStatusDataProp('status'),
  (status) => status || '',
);

const selectAllowedOperationIsInAutoapyFlow = () => createSelector(
  selectStatusDataProp('allowedOperation'),
  makeSelectAutopay('isInAutopayFlow'),
  (allowedOperation, isInAutopayFlow) =>
    (isInAutopayFlow === undefined || !isInAutopayFlow) &&
    (allowedOperation === 'Request_Enable' ||
     allowedOperation === 'Request_Change_Or_Stop'),
);

export default makeSelectAutopay;
export {
  selectAllowedOperationIsInAutoapyFlow,
  selectAllowedSetting,
  selectAmountDrawerProps,
  selectAutopayFlow,
  selectAutopayStatus,
  selectAvailableBeginDates,
  selectAvailableDelayDays,
  selectCancelConfirmation,
  selectConfirmationData,
  selectDisclosureLanguage,
  selectEditAutopayReviewData,
  selectError,
  selectForm,
  selectFormattedBeginDate,
  selectFormattedDelayDays,
  selectFormErrors,
  selectIsAbleToAccessAutopay,
  selectIsAutopayEnabledOrPending,
  selectIsEligibleForAutopay,
  selectIsModalOpen,
  selectMaxDelayDays,
  selectPaymentAmount,
  selectRequestBody,
  selectSelectedAdditionalPrincipal,
  selectSelectedBeginDate,
  selectSelectedDelayDays,
  selectSetupAutopayReviewData,
  selectStatusDataProp,
  selectStatusMessageProps,
  selectStatusProp,
  selectSummaryScheduledDateString,
};
